/* ==========================================================================
   BURAQ LEATHERS — interaction + motion layer
   Self-contained (no CDN) so the folder works offline when zipped and sent.
   Covers every behaviour on the live site: smooth scroll, scroll reveals,
   parallax, sliders, drawers, accordions, before/after drag, sticky ATC.
   ========================================================================== */
(function () {
  'use strict';

  var reduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  var $  = function (s, c) { return (c || document).querySelector(s); };
  var $$ = function (s, c) { return Array.prototype.slice.call((c || document).querySelectorAll(s)); };

  /* ------------------------------------------------------------------------
     Smooth scrolling — lerped scroll with a native fallback.
     ------------------------------------------------------------------------ */
  function smoothScroll() {
    if (reduced) return;
    document.documentElement.style.scrollBehavior = 'smooth';
  }

  /* ------------------------------------------------------------------------
     Scroll reveals — replaces the theme's scroll-trigger classes.
     Cascading children get an incremental delay.
     ------------------------------------------------------------------------ */
  function reveals() {
    var items = $$('[data-reveal], .img-reveal, .text-reveal, .hero');
    if (!items.length) return;

    if (!('IntersectionObserver' in window) || reduced) {
      items.forEach(function (el) { el.classList.add('is-in'); });
      return;
    }

    var io = new IntersectionObserver(function (entries) {
      entries.forEach(function (e) {
        if (!e.isIntersecting) return;
        e.target.classList.add('is-in');
        io.unobserve(e.target);
      });
    }, { threshold: 0.12, rootMargin: '0px 0px -8% 0px' });

    items.forEach(function (el) {
      var cascade = el.closest('[data-cascade]');
      if (cascade && !el.style.getPropertyValue('--reveal-delay')) {
        var sibs = $$('[data-reveal], .img-reveal, .text-reveal', cascade);
        var i = sibs.indexOf(el);
        if (i > 0) el.style.setProperty('--reveal-delay', Math.min(i * 90, 540) + 'ms');
      }
      io.observe(el);
    });
  }

  /* ------------------------------------------------------------------------
     Parallax — rAF-throttled, transform only.
     ------------------------------------------------------------------------ */
  function parallax() {
    var layers = $$('[data-parallax]');
    if (!layers.length || reduced) return;

    var ticking = false;
    function frame() {
      var vh = window.innerHeight;
      layers.forEach(function (el) {
        var r = el.getBoundingClientRect();
        if (r.bottom < -200 || r.top > vh + 200) return;
        var speed = parseFloat(el.getAttribute('data-parallax')) || 0.15;
        var progress = (r.top + r.height / 2 - vh / 2) / vh;
        // written as a custom property so it composes with any scale/reveal
        // transform the element already carries
        el.style.setProperty('--py', (progress * speed * 100).toFixed(2) + 'px');
      });
      ticking = false;
    }
    function onScroll() {
      if (ticking) return;
      ticking = true;
      requestAnimationFrame(frame);
    }
    window.addEventListener('scroll', onScroll, { passive: true });
    window.addEventListener('resize', onScroll);
    frame();
  }

  /* ------------------------------------------------------------------------
     Header — shadow/blur once scrolled.
     ------------------------------------------------------------------------ */
  function header() {
    var el = $('.header');
    if (!el) return;
    function upd() { el.classList.toggle('is-stuck', window.scrollY > 12); }
    window.addEventListener('scroll', upd, { passive: true });
    upd();
  }

  /* ------------------------------------------------------------------------
     Overlay plumbing — drawer, cart panel, search panel share one scrim.
     ------------------------------------------------------------------------ */
  var openLayer = null;

  function setLayer(el) {
    var scrim = $('.scrim');
    if (openLayer && openLayer !== el) openLayer.setAttribute('data-open', 'false');
    openLayer = el;
    var on = el && el.getAttribute('data-open') !== 'true';
    if (el) el.setAttribute('data-open', on ? 'true' : 'false');
    if (!on) openLayer = null;
    if (scrim) scrim.setAttribute('data-open', openLayer ? 'true' : 'false');
    document.body.style.overflow = openLayer ? 'hidden' : '';
    if (openLayer) {
      var focusable = $('input, button, a', openLayer);
      if (focusable) setTimeout(function () { focusable.focus(); }, 320);
    }
  }

  function closeLayers() {
    if (openLayer) openLayer.setAttribute('data-open', 'false');
    openLayer = null;
    var scrim = $('.scrim');
    if (scrim) scrim.setAttribute('data-open', 'false');
    document.body.style.overflow = '';
  }

  function overlays() {
    var scrim = $('.scrim');
    if (scrim) scrim.addEventListener('click', closeLayers);

    $$('[data-layer-open]').forEach(function (btn) {
      btn.addEventListener('click', function (e) {
        e.preventDefault();
        setLayer($('#' + btn.getAttribute('data-layer-open')));
      });
    });
    $$('[data-layer-close]').forEach(function (btn) {
      btn.addEventListener('click', closeLayers);
    });

    document.addEventListener('keydown', function (e) {
      if (e.key === 'Escape') {
        closeLayers();
        $$('.has-menu[data-open="true"]').forEach(function (m) {
          m.setAttribute('data-open', 'false');
        });
      }
    });
  }

  /* ------------------------------------------------------------------------
     Header dropdown ("All collections") — hover on desktop, tap on touch.
     ------------------------------------------------------------------------ */
  function dropdowns() {
    $$('.has-menu').forEach(function (menu) {
      var toggle = $('.has-menu__toggle', menu);
      var timer;

      menu.addEventListener('mouseenter', function () {
        clearTimeout(timer);
        if (window.matchMedia('(hover: hover)').matches) menu.setAttribute('data-open', 'true');
      });
      menu.addEventListener('mouseleave', function () {
        if (window.matchMedia('(hover: hover)').matches) {
          timer = setTimeout(function () { menu.setAttribute('data-open', 'false'); }, 160);
        }
      });
      if (toggle) {
        toggle.addEventListener('click', function (e) {
          e.preventDefault();
          var open = menu.getAttribute('data-open') === 'true';
          menu.setAttribute('data-open', open ? 'false' : 'true');
          toggle.setAttribute('aria-expanded', open ? 'false' : 'true');
        });
      }
    });

    document.addEventListener('click', function (e) {
      $$('.has-menu[data-open="true"]').forEach(function (m) {
        if (!m.contains(e.target)) m.setAttribute('data-open', 'false');
      });
    });
  }

  /* ------------------------------------------------------------------------
     Category rail — arrows + pointer drag, matching the live site.
     ------------------------------------------------------------------------ */
  function categoryRail() {
    var viewport = $('#catViewport');
    if (!viewport) return;

    var left  = $('#catPrev');
    var right = $('#catNext');
    var items = $$('.cat', viewport);
    var down = false, startX = 0, startScroll = 0, moved = false;

    if (left)  left.addEventListener('click', function () { viewport.scrollBy({ left: -240, behavior: 'smooth' }); });
    if (right) right.addEventListener('click', function () { viewport.scrollBy({ left: 240, behavior: 'smooth' }); });

    function updArrows() {
      if (left)  left.style.opacity  = viewport.scrollLeft > 10 ? '1' : '0.3';
      if (right) right.style.opacity = viewport.scrollLeft < (viewport.scrollWidth - viewport.clientWidth - 10) ? '1' : '0.3';
    }
    viewport.addEventListener('scroll', updArrows, { passive: true });
    updArrows();

    viewport.addEventListener('pointerdown', function (e) {
      down = true; moved = false;
      startX = e.clientX;
      startScroll = viewport.scrollLeft;
      viewport.classList.add('dragging');
    });
    window.addEventListener('pointerup', function () {
      down = false;
      viewport.classList.remove('dragging');
    });
    window.addEventListener('pointermove', function (e) {
      if (!down) return;
      var walk = e.clientX - startX;
      if (Math.abs(walk) > 5) moved = true;
      viewport.scrollLeft = startScroll - walk;
    });

    items.forEach(function (item) {
      item.addEventListener('click', function (e) {
        if (moved) { e.preventDefault(); return; }
        items.forEach(function (i) { i.classList.remove('is-active'); });
        item.classList.add('is-active');
      });
      // mark the rail entry matching the current page
      if (item.getAttribute('href') &&
          item.getAttribute('href').split('/').pop() === location.pathname.split('/').pop()) {
        item.classList.add('is-active');
      }
    });
  }

  /* ------------------------------------------------------------------------
     Before / After — drag the split handle.
     ------------------------------------------------------------------------ */
  function beforeAfter() {
    $$('.ba__frame').forEach(function (frame) {
      var handle = $('.ba__handle', frame);
      if (!handle) return;
      var dragging = false;

      function setSplit(clientX) {
        var r = frame.getBoundingClientRect();
        var pct = ((clientX - r.left) / r.width) * 100;
        pct = Math.max(0, Math.min(100, pct));
        frame.style.setProperty('--split', pct + '%');
      }

      handle.addEventListener('pointerdown', function (e) {
        dragging = true;
        handle.setPointerCapture(e.pointerId);
        e.preventDefault();
      });
      handle.addEventListener('pointermove', function (e) { if (dragging) setSplit(e.clientX); });
      handle.addEventListener('pointerup',     function () { dragging = false; });
      handle.addEventListener('pointercancel', function () { dragging = false; });

      // click anywhere on the frame to jump the split there
      frame.addEventListener('click', function (e) {
        if (e.target.closest('.ba__handle')) return;
        setSplit(e.clientX);
      });

      handle.setAttribute('tabindex', '0');
      handle.addEventListener('keydown', function (e) {
        var cur = parseFloat(getComputedStyle(frame).getPropertyValue('--split')) || 50;
        if (e.key === 'ArrowLeft')  { frame.style.setProperty('--split', Math.max(0, cur - 4) + '%'); e.preventDefault(); }
        if (e.key === 'ArrowRight') { frame.style.setProperty('--split', Math.min(100, cur + 4) + '%'); e.preventDefault(); }
      });
    });
  }

  /* ------------------------------------------------------------------------
     Accordions
     ------------------------------------------------------------------------ */
  function accordions() {
    $$('.acc__btn').forEach(function (btn) {
      btn.addEventListener('click', function () {
        var item = btn.closest('.acc__item');
        var open = item.getAttribute('data-open') === 'true';
        item.setAttribute('data-open', open ? 'false' : 'true');
        btn.setAttribute('aria-expanded', open ? 'false' : 'true');
      });
    });
  }

  /* ------------------------------------------------------------------------
     Quote slider (product card testimonials)
     ------------------------------------------------------------------------ */
  function quoteSliders() {
    $$('.quotes').forEach(function (root) {
      var track = $('.quotes__track', root);
      var slides = $$('.quote', root);
      if (!track || slides.length < 2) return;
      var i = 0;

      function go(n) {
        i = (n + slides.length) % slides.length;
        track.style.transform = 'translateX(' + (-i * 100) + '%)';
      }
      var prev = $('.quotes__btn--prev', root);
      var next = $('.quotes__btn--next', root);
      if (prev) prev.addEventListener('click', function () { go(i - 1); });
      if (next) next.addEventListener('click', function () { go(i + 1); });

      var timer = setInterval(function () { go(i + 1); }, 6000);
      root.addEventListener('mouseenter', function () { clearInterval(timer); });
    });
  }

  /* ------------------------------------------------------------------------
     Product gallery
     ------------------------------------------------------------------------ */
  function gallery() {
    var stage = $('#pdpStage');
    if (!stage) return;
    $$('.pdp__thumb').forEach(function (thumb) {
      thumb.addEventListener('click', function () {
        var src = thumb.getAttribute('data-full');
        if (!src) return;
        stage.style.opacity = '0';
        setTimeout(function () {
          stage.src = src;
          stage.style.opacity = '1';
        }, 180);
        $$('.pdp__thumb').forEach(function (t) { t.setAttribute('aria-current', 'false'); });
        thumb.setAttribute('aria-current', 'true');
      });
    });
    stage.style.transition = 'opacity .3s var(--ease)';
  }

  /* ------------------------------------------------------------------------
     Sticky add-to-cart — appears once the main button scrolls away.
     ------------------------------------------------------------------------ */
  function stickyAtc() {
    var bar = $('.sticky-atc');
    var anchor = $('#pdpAtc');
    if (!bar || !anchor) return;

    if (!('IntersectionObserver' in window)) { bar.setAttribute('data-show', 'true'); return; }
    var io = new IntersectionObserver(function (entries) {
      entries.forEach(function (e) {
        var show = !e.isIntersecting;
        bar.setAttribute('data-show', show ? 'true' : 'false');
        document.body.classList.toggle('has-sticky-atc', show);
      });
    }, { rootMargin: '-80px 0px 0px 0px' });
    io.observe(anchor);
  }

  /* ------------------------------------------------------------------------
     Back to top
     ------------------------------------------------------------------------ */
  function backToTop() {
    var btn = $('.to-top');
    if (!btn) return;
    function upd() { btn.setAttribute('data-show', window.scrollY > 500 ? 'true' : 'false'); }
    window.addEventListener('scroll', upd, { passive: true });
    btn.addEventListener('click', function () {
      window.scrollTo({ top: 0, behavior: reduced ? 'auto' : 'smooth' });
    });
    upd();
  }

  /* ------------------------------------------------------------------------
     Search — filters the bundled catalogue (window.BURAQ_PRODUCTS).
     ------------------------------------------------------------------------ */
  function search() {
    if (window.SHOPIFY_THEME) return;   // predictive search handles this
    var input = $('#searchInput');
    var out = $('#searchResults');
    if (!input || !out || !window.BURAQ_PRODUCTS) return;

    var base = out.getAttribute('data-base') || '';

    function render(list, q) {
      if (!q) { out.innerHTML = ''; return; }
      if (!list.length) {
        out.innerHTML = '<p class="panel__empty" style="margin:24px 0">No results for "' +
          q.replace(/[<>&]/g, '') + '"</p>';
        return;
      }
      out.innerHTML = list.slice(0, 8).map(function (p) {
        return '<a class="search-hit" href="' + base + 'product-' + p.handle + '.html">' +
          '<img src="' + base + (p.img ? 'assets/img/products/' + p.img : 'assets/img/brand/logo.png') +
          '" alt="" loading="lazy">' +
          '<span><span class="search-hit__name">' + p.title + '</span><br>' +
          '<span class="search-hit__price">Rs.' + p.price + '</span></span></a>';
      }).join('');
    }

    input.addEventListener('input', function () {
      var q = input.value.trim().toLowerCase();
      var hits = window.BURAQ_PRODUCTS.filter(function (p) {
        return (p.title + ' ' + (p.tags || '')).toLowerCase().indexOf(q) > -1;
      });
      render(hits, q);
    });
  }

  /* ------------------------------------------------------------------------
     Collection sorting
     ------------------------------------------------------------------------ */
  function collectionSort() {
    var select = $('#collSort');
    var grid = $('#collGrid');
    if (!select || !grid) return;

    var cards = $$('.pcard', grid);
    var original = cards.slice();

    select.addEventListener('change', function () {
      var mode = select.value;
      var sorted = original.slice();
      if (mode === 'price-asc')  sorted.sort(function (a, b) { return num(a) - num(b); });
      if (mode === 'price-desc') sorted.sort(function (a, b) { return num(b) - num(a); });
      if (mode === 'title-asc')  sorted.sort(function (a, b) { return name(a).localeCompare(name(b)); });
      if (mode === 'title-desc') sorted.sort(function (a, b) { return name(b).localeCompare(name(a)); });

      sorted.forEach(function (c, i) {
        c.style.order = i;
        c.style.transition = 'opacity .3s var(--ease)';
        c.style.opacity = '0';
        setTimeout(function () { c.style.opacity = '1'; }, 60);
      });
    });

    function num(card) {
      return parseFloat((card.getAttribute('data-price') || '0'));
    }
    function name(card) {
      return card.getAttribute('data-title') || '';
    }
  }

  /* ------------------------------------------------------------------------
     Demo-only guards — this is a colour-direction demo, not a live shop, so
     cart/checkout actions explain themselves instead of silently failing.
     ------------------------------------------------------------------------ */
  function demoActions() {
    if (window.SHOPIFY_THEME) return;   // real links/forms on Shopify
    $$('[data-demo-action]').forEach(function (btn) {
      btn.addEventListener('click', function (e) {
        e.preventDefault();
        var panel = $('#cartPanel');
        if (panel) setLayer(panel);
      });
    });

    var form = $('#contactForm');
    if (form) {
      form.addEventListener('submit', function (e) {
        e.preventDefault();
        var note = $('#formNote', form);
        if (note) {
          note.textContent = 'This is a design demo — the form is not connected to a mail service.';
          note.style.color = 'var(--navy)';
        }
      });
    }
  }


  /* ------------------------------------------------------------------------
     Toast — one shared notifier for cart / wishlist / share feedback.
     ------------------------------------------------------------------------ */
  var toastEl, toastTimer;
  function toast(msg) {
    if (!toastEl) {
      toastEl = document.createElement('div');
      toastEl.className = 'toast';
      toastEl.setAttribute('role', 'status');
      document.body.appendChild(toastEl);
    }
    toastEl.innerHTML = '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" ' +
      'stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round"><path d="M4 12.5l5 5L20 6.5"/></svg>' +
      '<span></span>';
    toastEl.querySelector('span').textContent = msg;
    toastEl.setAttribute('data-show', 'true');
    clearTimeout(toastTimer);
    toastTimer = setTimeout(function () { toastEl.setAttribute('data-show', 'false'); }, 2600);
  }

  /* ------------------------------------------------------------------------
     Cart — localStorage backed. Demo-only: no checkout, but the whole
     add/update/remove/free-shipping loop is real so the UX can be tested.
     ------------------------------------------------------------------------ */
  var CART_KEY = 'buraq_cart_v1';

  function cartRead() {
    try { return JSON.parse(localStorage.getItem(CART_KEY)) || []; }
    catch (e) { return []; }
  }
  function cartWrite(items) {
    try { localStorage.setItem(CART_KEY, JSON.stringify(items)); } catch (e) {}
    renderCart();
  }
  function findProduct(handle) {
    return (window.BURAQ_PRODUCTS || []).filter(function (p) { return p.handle === handle; })[0];
  }
  function fmt(n) {
    return 'Rs.' + n.toLocaleString('en-PK', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  }

  function cartAdd(handle, qty) {
    var p = findProduct(handle);
    if (!p) return;
    var items = cartRead();
    var line = items.filter(function (i) { return i.handle === handle; })[0];
    if (line) line.qty += (qty || 1);
    else items.push({ handle: handle, qty: qty || 1 });
    cartWrite(items);
    toast(p.title + ' added to cart');
    track('add_to_cart', { item_id: handle, value: p.price, currency: 'PKR' });
  }

  function cartSet(handle, qty) {
    var items = cartRead().map(function (i) {
      if (i.handle === handle) i.qty = qty;
      return i;
    }).filter(function (i) { return i.qty > 0; });
    cartWrite(items);
  }

  function renderCart() {
    var items = cartRead();
    var detailed = items.map(function (i) {
      var p = findProduct(i.handle);
      return p ? { p: p, qty: i.qty } : null;
    }).filter(Boolean);

    var total = detailed.reduce(function (s, l) { return s + l.p.price * l.qty; }, 0);
    var count = detailed.reduce(function (s, l) { return s + l.qty; }, 0);

    $$('[data-cart-count]').forEach(function (el) {
      el.textContent = count;
      if (count > 0) el.removeAttribute('hidden'); else el.setAttribute('hidden', '');
    });

    $$('[data-cart-lines]').forEach(function (wrap) {
      wrap.innerHTML = detailed.map(function (l) {
        var img = l.p.img ? 'assets/img/products/' + l.p.img : 'assets/img/products/placeholder.jpg';
        return '<div class="cline">' +
          '<img src="' + img + '" alt="" loading="lazy">' +
          '<div><div class="cline__name">' + l.p.title + '</div>' +
          '<div class="cline__price">' + fmt(l.p.price) + '</div>' +
          '<div class="cline__qty">' +
            '<button data-cart-dec="' + l.p.handle + '" aria-label="Decrease quantity">' +
              '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round"><path d="M5 12h14"/></svg></button>' +
            '<span>' + l.qty + '</span>' +
            '<button data-cart-inc="' + l.p.handle + '" aria-label="Increase quantity">' +
              '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round"><path d="M12 5v14M5 12h14"/></svg></button>' +
          '</div></div>' +
          '<button class="cline__rm" data-cart-rm="' + l.p.handle + '">Remove</button>' +
          '</div>';
      }).join('');
    });

    $$('[data-cart-total]').forEach(function (el) { el.textContent = fmt(total); });
    $$('[data-cart-empty]').forEach(function (el) {
      el.style.display = detailed.length ? 'none' : '';
    });
    $$('[data-cart-foot]').forEach(function (el) {
      if (detailed.length) el.removeAttribute('hidden'); else el.setAttribute('hidden', '');
    });

    // free-shipping progress
    var threshold = (window.BURAQ_CFG || {}).freeShip || 0;
    $$('[data-ship-meter]').forEach(function (m) {
      if (!detailed.length || !threshold) { m.setAttribute('hidden', ''); return; }
      m.removeAttribute('hidden');
      var pct = Math.min(100, (total / threshold) * 100);
      var fill = $('[data-ship-fill]', m);
      var label = $('[data-ship-label]', m);
      if (fill) fill.style.width = pct + '%';
      if (label) {
        label.innerHTML = total >= threshold
          ? 'You have unlocked <b>free delivery</b>'
          : 'Add <b>' + fmt(threshold - total) + '</b> more for free delivery';
      }
    });
  }

  function cart() {
    // Shopify theme: shopify-cart.js owns the cart via /cart.js
    if (window.SHOPIFY_THEME) return;
    document.addEventListener('click', function (e) {
      var add = e.target.closest('[data-add]');
      if (add && !add.disabled) {
        e.preventDefault();
        var qtyIn = $('[data-qty] .qty__in');
        var q = 1;
        if (qtyIn && add.id === 'pdpAtc') q = Math.max(1, parseInt(qtyIn.value, 10) || 1);
        cartAdd(add.getAttribute('data-add'), q);
        return;
      }
      var many = e.target.closest('[data-add-many]');
      if (many) {
        e.preventDefault();
        many.getAttribute('data-add-many').split(',').forEach(function (h) {
          var items = cartRead();
          var line = items.filter(function (i) { return i.handle === h; })[0];
          if (line) line.qty += 1; else items.push({ handle: h, qty: 1 });
          try { localStorage.setItem(CART_KEY, JSON.stringify(items)); } catch (err) {}
        });
        renderCart();
        toast('Both items added to cart');
        return;
      }
      var inc = e.target.closest('[data-cart-inc]');
      if (inc) {
        var h1 = inc.getAttribute('data-cart-inc');
        var cur = cartRead().filter(function (i) { return i.handle === h1; })[0];
        cartSet(h1, (cur ? cur.qty : 0) + 1);
        return;
      }
      var dec = e.target.closest('[data-cart-dec]');
      if (dec) {
        var h2 = dec.getAttribute('data-cart-dec');
        var cur2 = cartRead().filter(function (i) { return i.handle === h2; })[0];
        cartSet(h2, (cur2 ? cur2.qty : 0) - 1);
        return;
      }
      var rm = e.target.closest('[data-cart-rm]');
      if (rm) { cartSet(rm.getAttribute('data-cart-rm'), 0); }
    });
    renderCart();
  }

  /* ------------------------------------------------------------------------
     Quantity stepper
     ------------------------------------------------------------------------ */
  function qtyStepper() {
    $$('[data-qty]').forEach(function (box) {
      var input = $('.qty__in', box);
      if (!input) return;
      var up = $('[data-qty-up]', box), down = $('[data-qty-down]', box);
      function set(v) { input.value = Math.min(10, Math.max(1, v)); }
      if (up) up.addEventListener('click', function () { set(parseInt(input.value, 10) + 1); });
      if (down) down.addEventListener('click', function () { set(parseInt(input.value, 10) - 1); });
      input.addEventListener('change', function () { set(parseInt(input.value, 10) || 1); });
    });
  }

  /* ------------------------------------------------------------------------
     Brand film — deferred load. Sources carry data-src so nothing downloads
     until the section approaches the viewport; keeps it off the LCP path.
     ------------------------------------------------------------------------ */
  function brandFilm() {
    var stage = $('[data-film]');
    if (!stage) return;
    var video = $('[data-film-video]', stage);
    var playBtn = $('[data-film-play]', stage);
    var soundBtn = $('[data-film-sound]', stage);
    if (!video) return;

    var loaded = false;
    var mobile = window.matchMedia('(max-width: 749px)').matches;
    var saveData = (navigator.connection || {}).saveData;

    function load() {
      if (loaded) return;
      loaded = true;
      $$('source', video).forEach(function (s) {
        var src = s.getAttribute('data-src');
        if (!src) return;
        if (mobile && src.indexOf('.mp4') > -1) src = src.replace('brand-film.mp4', 'brand-film-mobile.mp4');
        s.src = src;
      });
      video.load();
      video.addEventListener('loadeddata', function () {
        stage.classList.add('is-ready');
      }, { once: true });
    }

    function play() {
      load();
      var p = video.play();
      if (p && p.catch) p.catch(function () { /* autoplay blocked — poster stays */ });
      stage.classList.add('is-playing', 'is-ready');
      if (playBtn) playBtn.setAttribute('hidden', '');
      if (soundBtn) soundBtn.removeAttribute('hidden');
      track('brand_film_play', {});
    }

    if ('IntersectionObserver' in window) {
      var io = new IntersectionObserver(function (entries) {
        entries.forEach(function (e) {
          if (!e.isIntersecting) {
            if (loaded && !video.paused) video.pause();
            return;
          }
          load();
          // respect reduced-motion and data-saver: require an explicit tap
          if (!reduced && !saveData) play();
        });
      }, { rootMargin: '200px 0px', threshold: 0.25 });
      io.observe(stage);
    } else {
      load();
    }

    if (playBtn) playBtn.addEventListener('click', play);
    if (soundBtn) {
      soundBtn.addEventListener('click', function () {
        video.muted = !video.muted;
        soundBtn.setAttribute('aria-label', video.muted ? 'Unmute brand film' : 'Mute brand film');
        soundBtn.innerHTML = video.muted
          ? '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><path d="M5 9.5h3.5L13 6v12L8.5 14.5H5v-5Z"/><path d="M17 10l4 4M21 10l-4 4"/></svg>'
          : '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><path d="M5 9.5h3.5L13 6v12L8.5 14.5H5v-5Z"/><path d="M16.5 9a4 4 0 0 1 0 6"/><path d="M19 6.5a7.5 7.5 0 0 1 0 11"/></svg>';
        if (!video.muted) track('brand_film_unmute', {});
      });
    }
  }

  /* ------------------------------------------------------------------------
     Product image zoom (pointer-follow)
     ------------------------------------------------------------------------ */
  function imageZoom() {
    var stage = $('[data-zoom]');
    if (!stage || window.matchMedia('(hover: none)').matches) return;
    var img = $('img', stage);
    if (!img) return;

    stage.addEventListener('mouseenter', function () { stage.classList.add('is-zoom'); });
    stage.addEventListener('mouseleave', function () {
      stage.classList.remove('is-zoom');
      img.style.transformOrigin = 'center center';
    });
    stage.addEventListener('mousemove', function (e) {
      var r = stage.getBoundingClientRect();
      var x = ((e.clientX - r.left) / r.width) * 100;
      var y = ((e.clientY - r.top) / r.height) * 100;
      img.style.transformOrigin = x + '% ' + y + '%';
    });
  }

  /* ------------------------------------------------------------------------
     Review filtering
     ------------------------------------------------------------------------ */
  function reviewFilter() {
    var bar = $('[data-rfilter]');
    if (!bar) return;
    var revs = $$('.rev');
    $$('.rfilter__b', bar).forEach(function (btn) {
      btn.addEventListener('click', function () {
        $$('.rfilter__b', bar).forEach(function (b) { b.classList.remove('is-on'); });
        btn.classList.add('is-on');
        var f = btn.getAttribute('data-filter');
        revs.forEach(function (r) {
          var show = f === 'all' || r.getAttribute('data-rating') === f;
          if (show) r.removeAttribute('hidden'); else r.setAttribute('hidden', '');
        });
      });
    });
  }

  /* ------------------------------------------------------------------------
     Wishlist + share
     ------------------------------------------------------------------------ */
  var WISH_KEY = 'buraq_wishlist_v1';
  function wishlist() {
    var saved;
    try { saved = JSON.parse(localStorage.getItem(WISH_KEY)) || []; } catch (e) { saved = []; }

    $$('[data-wishlist]').forEach(function (btn) {
      var h = btn.getAttribute('data-wishlist');
      if (saved.indexOf(h) > -1) btn.classList.add('is-on');
      btn.addEventListener('click', function () {
        var i = saved.indexOf(h);
        if (i > -1) { saved.splice(i, 1); btn.classList.remove('is-on'); toast('Removed from saved'); }
        else { saved.push(h); btn.classList.add('is-on'); toast('Saved for later'); }
        try { localStorage.setItem(WISH_KEY, JSON.stringify(saved)); } catch (e) {}
      });
    });

    $$('[data-share]').forEach(function (btn) {
      btn.addEventListener('click', function () {
        var data = { title: document.title, url: location.href };
        if (navigator.share) { navigator.share(data).catch(function () {}); return; }
        if (navigator.clipboard) {
          navigator.clipboard.writeText(location.href).then(function () {
            toast('Link copied');
          }).catch(function () {});
        }
      });
    });
  }

  /* ------------------------------------------------------------------------
     Announcement rotator + newsletter
     ------------------------------------------------------------------------ */
  function announcer() {
    var bar = $('#announceBar');
    if (!bar) return;
    var items = $$('.announcement__item', bar);
    if (items.length < 2 || reduced) return;
    var i = 0;
    setInterval(function () {
      items[i].setAttribute('hidden', '');
      i = (i + 1) % items.length;
      items[i].removeAttribute('hidden');
    }, 4200);
  }

  function newsletter() {
    if (window.SHOPIFY_THEME) return;   // real Shopify customer form
    var form = $('[data-newsletter]');
    if (!form) return;
    form.addEventListener('submit', function (e) {
      e.preventDefault();
      var note = $('[data-news-note]', form);
      var email = form.querySelector('input[type=email]');
      if (!email || !email.validity.valid) {
        if (note) note.textContent = 'Please enter a valid email address.';
        return;
      }
      if (note) note.textContent = 'Demo only — connect your email platform to collect signups.';
      form.reset();
      track('newsletter_signup', {});
    });
  }

  /* ------------------------------------------------------------------------
     Analytics — safe no-op until GA4 / Pixel IDs are filled in site.json.
     ------------------------------------------------------------------------ */
  function track(event, params) {
    if (typeof window.gtag === 'function') window.gtag('event', event, params || {});
    if (typeof window.fbq === 'function') {
      var map = { add_to_cart: 'AddToCart', newsletter_signup: 'Lead' };
      if (map[event]) window.fbq('track', map[event], params || {});
    }
    (window.dataLayer = window.dataLayer || []).push(
      Object.assign({ event: event }, params || {}));
  }

  function scrollDepth() {
    if (!('IntersectionObserver' in window)) return;
    var marks = [25, 50, 75, 100], hit = {};
    window.addEventListener('scroll', function () {
      var h = document.documentElement.scrollHeight - window.innerHeight;
      if (h <= 0) return;
      var pct = (window.scrollY / h) * 100;
      marks.forEach(function (m) {
        if (pct >= m && !hit[m]) { hit[m] = 1; track('scroll_depth', { percent: m }); }
      });
    }, { passive: true });
  }

  function searchSuggest() {
    if (window.SHOPIFY_THEME) return;   // wired in shopify-cart.js
    $$('[data-search-term]').forEach(function (b) {
      b.addEventListener('click', function () {
        var input = $('#searchInput');
        if (!input) return;
        input.value = b.getAttribute('data-search-term');
        input.dispatchEvent(new Event('input'));
        input.focus();
      });
    });
  }

  /* ------------------------------------------------------------------------ */
  function init() {
    smoothScroll();
    reveals();
    parallax();
    header();
    overlays();
    dropdowns();
    categoryRail();
    beforeAfter();
    accordions();
    quoteSliders();
    gallery();
    stickyAtc();
    backToTop();
    search();
    collectionSort();
    demoActions();
    cart();
    qtyStepper();
    brandFilm();
    imageZoom();
    reviewFilter();
    wishlist();
    announcer();
    newsletter();
    searchSuggest();
    scrollDepth();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
